import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import { format, subDays, subMonths } from 'date-fns';
import { API_BASE, apiFetch } from '../lib/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'react-toastify';
import { FaTrash, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

type DealerResponse = {
    id: number;
    distributor: string;
    concern: string | null;
    summary: string | null;
    intent: string | null;
    notes: string | null;
    text: string | null;
    created_at: string;
};

const STATIC_RANGES = createStaticRanges([
    {
        label: 'Last 7 days',
        range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }),
    },
    {
        label: 'Last 30 days',
        range: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }),
    },
    {
        label: 'Last 3 months',
        range: () => ({ startDate: subMonths(new Date(), 3), endDate: new Date() }),
    },
]);

const DealerResponses: React.FC = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<DealerResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [dismissingId, setDismissingId] = useState<number | null>(null);
    const [pendingDismiss, setPendingDismiss] = useState<DealerResponse | null>(null);

    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
        startDate: null,
        endDate: null,
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateRange.startDate) params.set('from', format(dateRange.startDate, 'yyyy-MM-dd'));
            if (dateRange.endDate)   params.set('to',   format(dateRange.endDate,   'yyyy-MM-dd'));

            const url = `${API_BASE}/api/dealer-responses${params.toString() ? '?' + params.toString() : ''}`;
            const res = await apiFetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data?.error ?? 'Failed to load dealer responses');
                return;
            }
            setRows(data.rows ?? []);
        } catch {
            toast.error('Failed to load dealer responses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [dateRange]);

    const confirmDismiss = async () => {
        const row = pendingDismiss;
        if (!row) return;
        setDismissingId(row.id);
        try {
            const res = await apiFetch(`${API_BASE}/api/concerns/${row.id}`, { method: 'DELETE' });
            if (!res.ok) {
                toast.error('Failed to dismiss');
                return;
            }
            setRows(prev => prev.filter(r => r.id !== row.id));
        } catch {
            toast.error('Failed to dismiss');
        } finally {
            setDismissingId(null);
            setPendingDismiss(null);
        }
    };

    const clearDates = () => setDateRange({ startDate: null, endDate: null });

    const hasDateFilter = !!dateRange.startDate || !!dateRange.endDate;

    const dateLabel = () => {
        const { startDate, endDate } = dateRange;
        if (!startDate && !endDate) return 'Date range';
        if (startDate && endDate)
            return `${format(startDate, 'dd MMM yy')} – ${format(endDate, 'dd MMM yy')}`;
        if (startDate) return `From ${format(startDate, 'dd MMM yy')}`;
        return 'Date range';
    };

    const filtered = filter.trim()
        ? rows.filter(r =>
            r.distributor.toLowerCase().includes(filter.toLowerCase()) ||
            (r.intent ?? '').toLowerCase().includes(filter.toLowerCase()) ||
            (r.concern ?? '').toLowerCase().includes(filter.toLowerCase()) ||
            (r.notes ?? '').toLowerCase().includes(filter.toLowerCase()) ||
            (r.summary ?? '').toLowerCase().includes(filter.toLowerCase())
        )
        : rows;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Dealer Responses</h1>
                    <p className="text-sm text-gray-500 mt-0.5">All open responses captured by the AI — latest first</p>
                </div>
                <span className="text-sm font-medium text-gray-600 bg-gray-200 px-3 py-1 rounded-full">{filtered.length} open</span>
            </div>

            {/* Filters card */}
            <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                <input
                    type="text"
                    placeholder="Filter by distributor, intent, concern…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-white shadow-md focus:outline-none focus:border-gray-500 transition-colors"
                />

                {/* Date range picker */}
                <div className="relative" ref={pickerRef}>
                    <button
                        onClick={() => setPickerOpen(o => !o)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border shadow-md transition-colors cursor-pointer
                            ${hasDateFilter
                                ? 'bg-gray-700 text-white border-gray-700'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >
                        <FaCalendarAlt size={13} />
                        {dateLabel()}
                    </button>
                    {hasDateFilter && (
                        <button
                            onClick={clearDates}
                            className="absolute -top-1.5 -right-1.5 bg-gray-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-500 transition-colors cursor-pointer"
                            title="Clear dates"
                        >
                            <FaTimes size={8} />
                        </button>
                    )}
                    {pickerOpen && (
                        <div className="absolute z-50 mt-2 shadow-xl rounded-lg overflow-hidden border border-gray-200">
                            <DateRangePicker
                                ranges={[{
                                    startDate: dateRange.startDate ?? new Date(),
                                    endDate:   dateRange.endDate   ?? new Date(),
                                    key: 'selection',
                                }]}
                                onChange={item => {
                                    const sel = item.selection;
                                    const start = sel.startDate ?? null;
                                    const end   = sel.endDate   ?? null;
                                    setDateRange({ startDate: start, endDate: end });
                                    if (start && end && start.getTime() !== end.getTime()) {
                                        setPickerOpen(false);
                                    }
                                }}
                                staticRanges={STATIC_RANGES}
                                inputRanges={[]}
                                moveRangeOnFirstSelection={false}
                                maxDate={new Date()}
                                rangeColors={['#374151']}
                                months={2}
                                direction="horizontal"
                            />
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-700 text-white">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium">Distributor</th>
                                <th className="px-4 py-3 text-left font-medium">Intent / Concern</th>
                                <th className="px-4 py-3 text-left font-medium">Notes / Summary</th>
                                <th className="px-4 py-3 text-left font-medium">Dealer Said</th>
                                <th className="px-4 py-3 text-left font-medium">Date</th>
                                <th className="px-4 py-3 text-center font-medium w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                                        No open dealer responses.
                                    </td>
                                </tr>
                            ) : filtered.map((row, i) => (
                                <tr key={row.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/chat?distributor=${encodeURIComponent(row.distributor)}`)}
                                            className="font-medium text-gray-800 hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                                        >
                                            {row.distributor}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">
                                        {row.intent && <span className="inline-block bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-xs font-medium">{row.intent}</span>}
                                        {row.concern && <span className="inline-block bg-blue-50 text-blue-700 rounded px-2 py-0.5 text-xs font-medium">{row.concern}</span>}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                                        {row.notes && <p className="text-xs">{row.notes}</p>}
                                        {row.summary && <p className="text-xs">{row.summary}</p>}
                                        {!row.notes && !row.summary && <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                                        {row.text
                                            ? <p className="text-xs line-clamp-2">{row.text}</p>
                                            : <span className="text-gray-300">—</span>
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                                        {new Date(row.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => setPendingDismiss(row)}
                                            disabled={dismissingId === row.id}
                                            className="text-gray-400 hover:text-red-500 disabled:opacity-40 cursor-pointer transition-colors"
                                            title="Dismiss"
                                        >
                                            <FaTrash size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmDialog
                open={!!pendingDismiss}
                title="Dismiss response"
                message={<>Dismiss the response from <span className="font-medium text-gray-800">{pendingDismiss?.distributor}</span>?</>}
                confirmLabel="Dismiss"
                danger
                busy={dismissingId !== null}
                onConfirm={confirmDismiss}
                onCancel={() => { if (dismissingId === null) setPendingDismiss(null); }}
            />
        </div>
    );
};

export default DealerResponses;
